import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PrismaticMessageEvent } from '@prismatic-io/embedded';
import {
  INTEGRATION_ID_TO_PRISMATIC_NAME,
  PrismaticClient,
  PrismaticIntegrationNode,
  createPrismaticClient,
} from 'studio5/clients/prismatic';
import {
  Integration,
  IntegrationVendor,
} from 'studio5/pages/integrations/integrations.types';
import { isIntegrationId } from 'studio5/pages/integrations/metadata/lib/integration-operators';
import { useAuth } from './auth';

const PrismaticContext = createContext<PrismaticClient>({} as PrismaticClient);

export default function PrismaticProvider({
  children,
}: PropsWithChildren<unknown>) {
  const { client: authClient } = useAuth();

  const prismaticClient = useMemo(() => {
    return createPrismaticClient(authClient);
  }, [authClient]);

  return (
    <PrismaticContext.Provider value={prismaticClient}>
      {children}
    </PrismaticContext.Provider>
  );
}

const getPrismaticIntegrationName = (integration: Integration) => {
  if (isIntegrationId(integration.id)) {
    const prismaticName = INTEGRATION_ID_TO_PRISMATIC_NAME[integration.id];
    if (prismaticName) {
      return prismaticName;
    }
  }
  return `${integration.name} Integration`;
};

/**
 * Custom hook to interact with the integrations backed by Prismatic.
 * Besides fetching and checking if integrations are enabled, it also
 * binds an event listener to ensure the integrations are synced with the
 * Prismatic platform.
 */
export function usePrismatic() {
  const prismaticClient = useContext(PrismaticContext);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [prismaticIntegrations, setPrismaticIntegrations] = useState<
    PrismaticIntegrationNode[]
  >([]);

  const loadPrismaticIntegrations = useCallback(async () => {
    try {
      setIsLoadingIntegrations(true);
      const integrations = await prismaticClient.getAccountIntegrations();
      setPrismaticIntegrations(integrations);
    } catch {
      throw new Error('Failed to fetch Prismatic Integrations');
    } finally {
      setIsLoadingIntegrations(false);
    }
  }, [prismaticClient]);

  const subscribeToPrismaticEvents = useCallback(() => {
    window.addEventListener('message', async event => {
      const {
        INSTANCE_CREATED,
        INSTANCE_DELETED,
        INSTANCE_CONFIGURATION_CLOSED,
        INSTANCE_DEPLOYED,
      } = PrismaticMessageEvent;
      const eventMessage = event.data.event;

      if (
        [
          INSTANCE_CREATED,
          INSTANCE_DELETED,
          INSTANCE_CONFIGURATION_CLOSED,
          INSTANCE_DEPLOYED,
        ].includes(eventMessage)
      ) {
        await loadPrismaticIntegrations();
      }
    });
  }, [loadPrismaticIntegrations]);

  /* Loads prismatic integrations when the `usePrismatic` hook is called */
  useEffect(() => {
    loadPrismaticIntegrations();
    subscribeToPrismaticEvents();

    return () => {
      window.removeEventListener('message', subscribeToPrismaticEvents);
    };
  }, [loadPrismaticIntegrations, subscribeToPrismaticEvents]);

  const configureInstance = useCallback(
    async (integration: Integration) => {
      const isPrismatic =
        integration.integrationVendor === IntegrationVendor.PRISMATIC;

      if (!isPrismatic) return;

      const prismaticIntegrationName = getPrismaticIntegrationName(integration);

      try {
        await prismaticClient.configureInstance(prismaticIntegrationName);
      } catch {
        throw new Error(
          `An error occurred while configuring ${integration.name}. Please try again.`
        );
      }
    },
    [prismaticClient]
  );

  const isIntegrationEnabled = useCallback(
    (integration: Integration) => {
      const integrationName = getPrismaticIntegrationName(integration);
      const prismaticIntegration = prismaticIntegrations.find(
        ({ integration: { name } }) => name === integrationName
      );

      if (prismaticIntegration) {
        return prismaticIntegration.enabled;
      }

      return false;
    },
    [prismaticIntegrations]
  );

  return {
    prismaticIntegrations,
    configureInstance,
    isLoadingIntegrations,
    isIntegrationEnabled,
  };
}
